"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Building2, Clock, Coffee, ShieldCheck } from "lucide-react";
import { format } from "date-fns";

const staticDirectory: DirectoryEntry[] = [
  {
    code: "YYC",
    name: "Calgary",
    employees: [
      { id: "johan", name: "Johan Alvarez Granjas" },
      { id: "neng", name: "Neng Pau" },
      { id: "rodion", name: "Rodion Dubinin" },
      { id: "ryan", name: "Ryan Oshiro" },
    ],
  },
  {
    code: "YEG",
    name: "Edmonton",
    employees: [
      { id: "julian", name: "Julian Gouldhauke" },
      { id: "sergiy", name: "Sergiy Milev" },
    ],
  },
];

type PunchAction = "clock-in" | "clock-out";

type Banner = { type: "info" | "success" | "error"; text: string } | null;

type DirectoryEntry = {
  code: string;
  name: string;
  employees: { id: string; name: string; shortCode?: string }[];
};

export default function KioskPage() {
  const [now, setNow] = useState<Date | null>(null);
  const [location, setLocation] = useState(staticDirectory[0]?.code ?? "YYC");
  const [employeeId, setEmployeeId] = useState(
    staticDirectory[0]?.employees[0]?.id ?? "",
  );
  const [pin, setPin] = useState("");
  const [action, setAction] = useState<PunchAction>("clock-in");
  const [skipLunch, setSkipLunch] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [submitting, setSubmitting] = useState(false);
  const [directory, setDirectory] = useState<DirectoryEntry[]>(staticDirectory);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directoryLoaded, setDirectoryLoaded] = useState(false);
  const locationRef = useRef(location);
  const employeeIdRef = useRef(employeeId);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const feedbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [feedback, setFeedback] = useState<"success" | "error" | null>(null);
  const [lastPunch, setLastPunch] = useState<{
    name: string;
    action: PunchAction;
    timestamp: string;
    location: string;
  } | null>(null);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    employeeIdRef.current = employeeId;
  }, [employeeId]);


  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadDirectory() {
      try {
        const res = await fetch("/api/directory");
        if (!res.ok) {
          throw new Error("Failed to load directory");
        }
        const payload = await res.json();
        if (payload.ok !== true || !Array.isArray(payload.locations)) {
          throw new Error(payload?.error?.message ?? "Invalid directory payload");
        }

        if (cancelled) return;

        const normalized: DirectoryEntry[] = payload.locations.map((loc: any) => ({
          code: loc.code,
          name: loc.name ?? loc.label ?? loc.code,
          employees: Array.isArray(loc.employees)
            ? loc.employees.map((emp: any) => ({
                id: emp.id,
                name: emp.name,
                shortCode: emp.shortCode,
              }))
            : [],
        }));

        setDirectory(normalized);

        const nextLocation =
          normalized.find((loc) => loc.code === locationRef.current)?.code ??
          normalized[0]?.code ??
          locationRef.current;

        const targetLocation = normalized.find((loc) => loc.code === nextLocation);
        const nextEmployee =
          targetLocation?.employees?.find((emp) => emp.id === employeeIdRef.current)?.id ??
          targetLocation?.employees?.[0]?.id ??
          employeeIdRef.current;

        setLocation(nextLocation);
        setEmployeeId(nextEmployee);
        setDirectoryError(null);
      } catch (error) {
        if (!cancelled) {
          setDirectoryError(
            error instanceof Error ? error.message : "Could not load the crew list.",
          );
        }
      } finally {
        if (!cancelled) {
          setDirectoryLoaded(true);
        }
      }
    }

    loadDirectory();

    return () => {
      cancelled = true;
    };
  }, []);

  const dayLabel = useMemo(() => (now ? format(now, "EEEE, MMMM d") : "—"), [now]);
  const timeLabel = useMemo(() => (now ? format(now, "h:mm:ss a") : "—"), [now]);

  const selectedLocation = directory.find((entry) => entry.code === location);
  const employeeRecord = selectedLocation?.employees.find(
    (emp) => emp.id === employeeId,
  );

  const playTone = useCallback(
    (kind: "success" | "error") => {
      if (typeof window === "undefined") return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        ctx.resume();
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      const locationFrequencyMap: Record<string, number> = { YYC: 880, YEG: 660 };
      const baseFrequency = locationFrequencyMap[location] ?? 760;
      oscillator.frequency.value = kind === "success" ? baseFrequency : 220;
      gain.gain.value = kind === "success" ? 0.15 : 0.2;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      oscillator.stop(ctx.currentTime + 0.35);
    },
    [location],
  );

  const triggerFeedback = useCallback((kind: "success" | "error") => {
    setFeedback(kind);
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), 700);
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close();
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!employeeRecord) {
      setBanner({ type: "error", text: "Select a team member." });
      return;
    }
    if (pin.length !== 4) {
      setBanner({ type: "error", text: "Enter the 4-digit PIN." });
      return;
    }

    setSubmitting(true);
    setBanner({ type: "info", text: "Submitting..." });

    try {
      const res = await fetch("/api/punch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employeeId: employeeRecord.id,
          locationCode: location,
          pin,
          action,
          skipLunch,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.ok !== true) {
        const message =
          typeof json?.error?.message === "string"
            ? json.error.message
            : "Request failed";
        throw new Error(message);
      }
      setBanner({
        type: "success",
        text:
          json.message ??
          `${employeeRecord.name} ${
            action === "clock-in" ? "clocked in" : "clocked out"
          } just now.`,
      });
      triggerFeedback("success");
      playTone("success");
      setLastPunch({
        name: employeeRecord.name,
        action,
        timestamp: json.timestamp ?? new Date().toISOString(),
        location,
      });
      setPin("");
      setSkipLunch(false);
    } catch (error) {
      triggerFeedback("error");
      playTone("error");
      setBanner({
        type: "error",
        text:
          error instanceof Error
            ? error.message
            : "Could not reach the punch service. We’ll keep retrying.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-100/60 to-white px-4 py-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 rounded-3xl bg-white/90 p-8 shadow-xl shadow-brand-900/5 ring-1 ring-brand-900/5 lg:flex-row">
        <section className="flex flex-1 flex-col gap-6">
          <div className="text-sm uppercase tracking-[0.35em] text-brand-700">Shed Solutions</div>
          <h1 className="font-[var(--font-display)] text-4xl text-brand-900 sm:text-5xl">
            Crew Punch Clock
          </h1>
          <p className="text-lg text-stone-600">
            Select your yard, confirm your name, and enter your 4-digit code. Lunch is removed automatically unless you mark “No lunch today” when clocking out.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <InfoCard icon={<Clock className="size-5" />} title={timeLabel} subtitle={dayLabel} />
            <InfoCard icon={<Building2 className="size-5" />} title={selectedLocation?.name ?? "—"} subtitle="Current location" />
          </div>
          <div className="space-y-3 rounded-2xl border border-brand-100/80 bg-brand-100/40 p-5 text-sm text-brand-900">
            <div className="flex items-start gap-3">
              <Coffee className="mt-1 size-4" />
              <p>
                <strong>Lunch policy:</strong> 30 minutes unpaid when shifts exceed 5.5h. If you worked through lunch, check the box before clocking out.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-1 size-4" />
              <p>
                Managers can adjust times later—every change is logged for payroll audit.
              </p>
            </div>
          </div>
        </section>

        <section className="flex flex-1 flex-col gap-6 rounded-2xl border border-brand-100/60 bg-white p-6 shadow-lg">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-stone-600">Location</label>
            <select
              className="w-full rounded-xl border border-brand-100 px-4 py-3 text-base focus:border-brand-500 focus:outline-none"
              value={location}
              onChange={(event) => {
                const value = event.target.value;
                setLocation(value);
                const loc = directory.find((entry) => entry.code === value);
                setEmployeeId(loc?.employees[0]?.id ?? "");
              }}
            >
              {directory.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>

          {directoryError && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {directoryError}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-semibold text-stone-600">Team member</label>
            <select
              className="w-full rounded-xl border border-brand-100 px-4 py-3 text-base focus:border-brand-500 focus:outline-none"
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              {selectedLocation?.employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div
            className={`space-y-2 rounded-2xl border border-transparent p-2 transition ${feedback === "success" ? "feedback-success" : feedback === "error" ? "feedback-error" : ""}`}
          >
            <label className="text-sm font-semibold text-stone-600">4-digit PIN</label>
            <input
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(event) => {
                const next = event.target.value.replace(/[^0-9]/g, "");
                setPin(next);
              }}
              className="w-full rounded-xl border border-brand-100 px-4 py-3 text-center text-2xl tracking-[0.5em] focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div className="flex gap-4">
            <ActionButton
              active={action === "clock-in"}
              label="Clock In"
              description="Start shift"
              onClick={() => setAction("clock-in")}
            />
            <ActionButton
              active={action === "clock-out"}
              label="Clock Out"
              description="End shift"
              onClick={() => setAction("clock-out")}
            />
          </div>

          <label className="inline-flex items-center gap-3 rounded-xl border border-brand-100 px-4 py-3 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={skipLunch}
              onChange={(event) => setSkipLunch(event.target.checked)}
              className="size-4 rounded border-brand-500 text-brand-500 focus:ring-brand-500"
            />
            No lunch break taken today
          </label>

          {lastPunch && (
            <div className="flex items-center justify-between rounded-xl border border-brand-100 bg-brand-50/80 px-4 py-3 text-sm text-stone-700">
              <div>
                <p className="font-semibold text-brand-900">
                  {lastPunch.name} {lastPunch.action === "clock-in" ? "clocked in" : "clocked out"}
                </p>
                <p className="text-xs text-stone-500">
                  {format(new Date(lastPunch.timestamp), "MMM d, h:mm a")} · {lastPunch.location}
                </p>
              </div>
              <span className="text-xs uppercase tracking-wide text-brand-700">Last punch</span>
            </div>
          )}

          {banner && (
            <div
              className={`rounded-xl px-4 py-3 text-sm ${
                banner.type === "success"
                  ? "border border-brand-300 bg-brand-100 text-brand-900"
                  : banner.type === "error"
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-brand-100 bg-brand-100/60 text-brand-900"
              }`}
            >
              {banner.text}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-2xl bg-brand-500 px-4 py-4 text-lg font-semibold text-white shadow-brand-500/40 transition hover:bg-brand-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Saving..." : `Confirm ${action === "clock-in" ? "Clock In" : "Clock Out"}`}
          </button>
        </section>
      </div>
    </div>
  );
}

function InfoCard({
  icon,
  title,
  subtitle,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-brand-100 bg-white/80 px-4 py-3">
      <div className="flex size-10 items-center justify-center rounded-full bg-brand-100 text-brand-700">
        {icon}
      </div>
      <div>
        <div className="text-lg font-semibold text-brand-900">{title}</div>
        <div className="text-sm text-stone-600">{subtitle}</div>
      </div>
    </div>
  );
}

function ActionButton({
  label,
  description,
  active,
  onClick,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex-1 rounded-2xl border px-4 py-4 text-left transition ${
        active
          ? "border-brand-500 bg-brand-100/80 text-brand-900 shadow-brand-500/20"
          : "border-brand-100 text-stone-600 hover:border-brand-200"
      }`}
    >
      <div className="text-lg font-semibold">{label}</div>
      <div className="text-sm">{description}</div>
    </button>
  );
}
